/**
 * Build / enrich cart line-item product properties for Superbundle (fly) adds.
 * Values align with snippets/cart-line-item-properties.liquid.
 */
(function () {
  const STOREFRONT_API_PATH = '/api/2025-01/graphql.json';
  const PRODUCT_PROP_KEYS = [
    '_product_available_quantity',
    '_product_price',
    '_product_tag',
    '_preorder',
    '_l1_category_name',
    '_l2_category_name',
    '_l3_category_name',
  ];

  const VARIANT_NODES_QUERY = `
    query CartLineItemVariantProps($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on ProductVariant {
          id
          quantityAvailable
          requiresShipping
          compareAtPrice { amount }
          product {
            tags
            parentName: metafield(namespace: "custom", key: "parent_name") { value }
            subCatName: metafield(namespace: "custom", key: "sub_cat_name") { value }
            subSubCatName: metafield(namespace: "custom", key: "sub_sub_cat_name") { value }
            productRibbons: metafield(namespace: "custom", key: "product_ribbons") {
              reference {
                ... on Metaobject {
                  fields { key value }
                }
              }
            }
          }
        }
      }
    }
  `;

  /**
   * @param {string | number} id
   * @returns {string}
   */
  function toVariantGid(id) {
    return 'gid://shopify/ProductVariant/' + String(id);
  }

  /**
   * @param {string} gid
   * @returns {string}
   */
  function fromVariantGid(gid) {
    return String(gid || '').replace('gid://shopify/ProductVariant/', '');
  }

  /**
   * @param {Record<string, any> | null | undefined} product
   * @returns {string}
   */
  function ribbonNameFromProduct(product) {
    const fields =
      product &&
      product.productRibbons &&
      product.productRibbons.reference &&
      product.productRibbons.reference.fields;
    if (!Array.isArray(fields)) return '';
    const match = fields.find((field) => field && field.key === 'ribbons_name');
    return match && match.value ? String(match.value) : '';
  }

  /**
   * Prefer Shopify product tags; fall back to ribbon metafield.
   * @param {Record<string, any> | null | undefined} product
   * @returns {string}
   */
  function productTagFromProduct(product) {
    const tags = Array.isArray(product && product.tags) ? product.tags.filter(Boolean) : [];
    if (tags.length) return tags.join(', ');
    return ribbonNameFromProduct(product);
  }

  /**
   * Storefront money amount → Shopify cents (number).
   * @param {string | number | null | undefined} amount
   * @returns {number}
   */
  function compareAtToCents(amount) {
    if (amount == null || amount === '') return 0;
    const parsed = Number(amount);
    if (Number.isNaN(parsed)) return 0;
    return Math.round(parsed * 100);
  }

  /**
   * @param {Record<string, any> | null | undefined} node
   * @returns {Record<string, string | number>}
   */
  function buildProductPropsFromVariantNode(node) {
    if (!node) return {};
    const product = node.product || {};
    const tags = Array.isArray(product.tags) ? product.tags : [];
    return {
      _product_available_quantity:
        node.quantityAvailable != null && Number.isFinite(Number(node.quantityAvailable))
          ? Number(node.quantityAvailable)
          : 0,
      _product_price: compareAtToCents(node.compareAtPrice && node.compareAtPrice.amount),
      _product_tag: productTagFromProduct(product),
      _preorder: tags.indexOf('Pre_Order') !== -1 ? 'yes' : 'no',
      _l1_category_name: (product.parentName && product.parentName.value) || '',
      _l2_category_name: (product.subCatName && product.subCatName.value) || '',
      _l3_category_name: (product.subSubCatName && product.subSubCatName.value) || '',
    };
  }

  /**
   * @param {Array<Record<string, any>>} items
   * @returns {boolean}
   */
  function itemsNeedProductProps(items) {
    return items.some((item) => {
      const props = item && item.properties;
      if (!props) return true;
      return PRODUCT_PROP_KEYS.some((key) => !(key in props));
    });
  }

  /**
   * @param {Array<string|number>} variantIds
   * @param {string} storefrontToken
   * @returns {Promise<Array<Record<string, any>>>}
   */
  function fetchVariantNodes(variantIds, storefrontToken) {
    const ids = variantIds.map(toVariantGid);
    return fetch(STOREFRONT_API_PATH, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontToken,
      },
      body: JSON.stringify({
        query: VARIANT_NODES_QUERY,
        variables: { ids: ids },
      }),
    })
      .then((response) => response.json())
      .then((result) => {
        const nodes = result && result.data && result.data.nodes;
        return Array.isArray(nodes) ? nodes.filter(Boolean) : [];
      });
  }

  /**
   * @param {Array<Record<string, any>>} items
   * @param {string} storefrontToken
   * @returns {Promise<Array<Record<string, any>>>}
   */
  function enrichFlyCartItems(items, storefrontToken) {
    if (!Array.isArray(items) || !items.length || !storefrontToken) {
      return Promise.resolve(items);
    }

    const needsDigital = items.some((item) => !(item.properties && '_digital_book' in item.properties));
    const needsPurchaseType = items.some((item) => !(item.properties && '_purchase_type' in item.properties));
    const needsProduct = itemsNeedProductProps(items);
    if (!needsDigital && !needsProduct) {
      if (!needsPurchaseType) return Promise.resolve(items);
      return Promise.resolve(
        items.map((item) => {
          const properties = Object.assign({}, item.properties || {});
          if (!('_purchase_type' in properties)) properties._purchase_type = 'null';
          return Object.assign({}, item, { properties: properties });
        })
      );
    }

    const variantIds = items.map((item) => item.id).filter(Boolean);
    return fetchVariantNodes(variantIds, storefrontToken)
      .then((nodes) => {
        /** @type {Record<string, Record<string, any>>} */
        const byId = {};
        nodes.forEach((node) => {
          if (!node || !node.id) return;
          byId[fromVariantGid(node.id)] = node;
        });

        return items.map((item) => {
          const node = byId[String(item.id)];
          const existing = Object.assign({}, item.properties || {});
          const productProps = needsProduct ? buildProductPropsFromVariantNode(node) : {};
          const nextProps = Object.assign({}, existing, productProps);

          if (node && !('_digital_book' in nextProps)) {
            nextProps._digital_book = node.requiresShipping === false ? 'true' : 'false';
          }
          if (!('_purchase_type' in nextProps)) {
            nextProps._purchase_type = 'null';
          }

          return Object.assign({}, item, { properties: nextProps });
        });
      })
      .catch(() => items);
  }

  /**
   * @param {Record<string, any>} item
   * @returns {boolean}
   */
  function cartItemNeedsProductProps(item) {
    const props = item && item.properties;
    if (!props) return true;
    return PRODUCT_PROP_KEYS.some((key) => !(key in props));
  }

  /**
   * After Fly Bundles / GFB widget add, the parent line often has OfferId but none of
   * the product analytics properties. Patch those lines via cart/change.js.
   * @param {Array<Record<string, any>>} cartItems
   * @param {string} storefrontToken
   * @returns {Promise<void>}
   */
  function patchMissingProductProps(cartItems, storefrontToken) {
    const targets = (Array.isArray(cartItems) ? cartItems : []).filter(
      (item) => item && item.key && cartItemNeedsProductProps(item)
    );
    if (!targets.length || !storefrontToken) {
      return Promise.resolve();
    }

    const variantIds = targets.map((item) => item.variant_id || item.id).filter(Boolean);
    return fetchVariantNodes(variantIds, storefrontToken)
      .then((nodes) => {
        const byId = {};
        nodes.forEach((node) => {
          if (!node || !node.id) return;
          byId[fromVariantGid(node.id)] = node;
        });

        return targets.reduce((chain, item) => {
          return chain.then(() => {
            const node = byId[String(item.variant_id || item.id)];
            const productProps = buildProductPropsFromVariantNode(node);
            if (!Object.keys(productProps).length) return null;
            const merged = Object.assign({}, item.properties || {}, productProps);
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: item.key,
                quantity: item.quantity,
                properties: merged,
              }),
            });
          });
        }, Promise.resolve());
      })
      .catch(() => undefined);
  }

  window.PWCartLineItemProps = {
    enrichFlyCartItems: enrichFlyCartItems,
    patchMissingProductProps: patchMissingProductProps,
    itemsNeedProductProps: itemsNeedProductProps,
    buildProductPropsFromVariantNode: buildProductPropsFromVariantNode,
  };
})();
